import React, { useEffect, useState } from 'react';
import TechCard from '../components/Tech/TechCard';
import AdCard from '../components/AdCard/AdCard';
import { fetchTechArticles } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { resolveImageUrl } from '../utils/imageUrl';
import './Tech.css';

const TechPage = () => {
  const { canAccessExclusiveContent } = useAuth();
  const [techArticles, setTechArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const getTechImageUrl = (article) => {
    const imagePath = article?.cover_image_url || article?.cover_image;
    return resolveImageUrl(imagePath);
  };

  // Extract unique tags from tech articles
  const allTags = techArticles.reduce((tags, article) => {
    if (article.tags && Array.isArray(article.tags)) {
      article.tags.forEach(tag => {
        if (!tags.includes(tag)) tags.push(tag);
      });
    }
    return tags;
  }, []).sort();

  // Filter articles based on search and tag
  const filteredArticles = techArticles.filter(article => {
    const matchesSearch = !searchTerm ||
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (article.excerpt && article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesTag = !selectedTag ||
      (article.tags && article.tags.includes(selectedTag));

    return matchesSearch && matchesTag;
  });

  // Separate sticky and regular articles
  const stickyArticles = filteredArticles.filter(article => article.is_sticky);
  const regularArticles = filteredArticles.filter(article => !article.is_sticky);
  const featuredArticles = regularArticles.filter(article => article.is_featured);
  const standardArticles = regularArticles.filter(article => !article.is_featured);

  useEffect(() => {
    const loadTechArticles = async () => {
      try {
        setLoading(true);
        const data = await fetchTechArticles();
        setTechArticles(data.data || data);
        setError(null);
      } catch (err) {
        console.error('Failed to load tech articles:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadTechArticles();
  }, []);

  const handleTagFilter = (tag) => {
    setSelectedTag(selectedTag === tag ? '' : tag);
  };

  const clearFilters = () => {
    setSelectedTag('');
    setSearchTerm('');
  };

  if (loading) {
    return (
      <div className="tech-page">
        <div className="tech-container">
          <div className="tech-main">
            <div className="tech-header">
              <h1>Tech Insights</h1>
              <p className="tech-subtitle">Technology trends, development insights, and digital innovation</p>
            </div>
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading tech articles...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tech-page">
        <div className="tech-container">
          <div className="tech-main">
            <div className="tech-header">
              <h1>Tech Insights</h1>
              <p className="tech-subtitle">Technology trends, development insights, and digital innovation</p>
            </div>
            <div className="error-container">
              <h3>Unable to load tech articles</h3>
              <p className="error-message">{error}</p>
              <button onClick={() => window.location.reload()} className="retry-btn">
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tech-page">
      <div className="tech-container">
        {/* Main Content Area */}
        <main className="tech-main">
          {/* Tech Header */}
          <div className="tech-header">
            <h1>Tech Insights</h1>
            <p className="tech-subtitle">Technology trends, development insights, and digital innovation</p>
          </div>

          {/* Results Info */}
          {(selectedTag || searchTerm) && (
            <div className="results-info">
              <p>
                {filteredArticles.length} article{filteredArticles.length !== 1 ? 's' : ''} found
                {selectedTag && ` for "${selectedTag}"`}
                {searchTerm && ` matching "${searchTerm}"`}
              </p>
            </div>
          )}

          {/* Sticky Articles */}
          {stickyArticles.length > 0 && (
            <section className="sticky-articles-section">
              <h3 className="section-title">
                <i className="fas fa-thumbtack"></i>
                Pinned Articles
              </h3>
              <div className="sticky-articles-grid">
                {stickyArticles.map(article => (
                  <TechCard
                    key={`sticky-${article.id}`}
                    tech={article}
                    variant="sticky"
                    canAccessExclusiveContent={canAccessExclusiveContent}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Featured Articles */}
          {featuredArticles.length > 0 && !selectedTag && !searchTerm && (
            <section className="featured-articles-section">
              <h3 className="section-title">
                <i className="fas fa-star"></i>
                Featured Tech
              </h3>
              <div className="featured-articles-grid">
                {featuredArticles.slice(0, 3).map(article => (
                  <TechCard
                    key={`featured-${article.id}`}
                    tech={article}
                    variant="featured"
                    canAccessExclusiveContent={canAccessExclusiveContent}
                  />
                ))}
              </div>
            </section>
          )}

          {/* All Articles */}
          <section className="all-articles-section">
            <h3 className="section-title">
              <i className="fas fa-microchip"></i>
              {selectedTag || searchTerm ? 'Filtered Articles' : 'Latest Tech'}
            </h3>

            {filteredArticles.length === 0 ? (
              <div className="no-articles">
                <i className="fas fa-search"></i>
                <h4>No articles found</h4>
                <p>Try adjusting your search or filter criteria</p>
                <button onClick={clearFilters} className="clear-filters-btn">
                  Show All Articles
                </button>
              </div>
            ) : (
              <div className="articles-grid">
                {standardArticles.map((article, index) => (
                  <React.Fragment key={article.id}>
                    <TechCard tech={article} canAccessExclusiveContent={canAccessExclusiveContent} />
                    {/* Insert ad after every 4th article */}
                    {(index + 1) % 4 === 0 && (
                      <div className="inline-ad-container">
                        <AdCard key={`ad-${index}`} />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </section>
        </main>

        {/* Sidebar */}
        <aside className="tech-sidebar">
          {/* Search and Filters */}
          <div className="sidebar-section tech-controls">
            <div className="search-section">
              <h4>Search Articles</h4>
              <input
                type="text"
                placeholder="Search tech articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            <div className="tags-section">
              <h4>Filter by Tech Topics</h4>
              <div className="tags-list">
                {allTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => handleTagFilter(tag)}
                    className={`tag-btn ${selectedTag === tag ? 'active' : ''}`}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
              {(selectedTag || searchTerm) && (
                <button onClick={clearFilters} className="clear-filters-btn">
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Recent Articles Sidebar */}
          <div className="sidebar-section recent-articles-section">
            <h4>Recent Articles</h4>
            <div className="sidebar-articles">
              {techArticles.slice(0, 5).map(article => (
                <div key={`sidebar-${article.id}`} className="sidebar-article">
                  {getTechImageUrl(article) && (
                    <img
                      src={getTechImageUrl(article)}
                      alt={article.title}
                      className="sidebar-article-image"
                    />
                  )}
                  <div className="sidebar-article-content">
                    <h5>{article.title}</h5>
                    <p className="sidebar-article-date">
                      {new Date(article.published_at || article.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Popular Tags */}
          <div className="sidebar-section popular-tags-section">
            <h4>Popular Tech Topics</h4>
            <div className="popular-tags">
              {allTags.slice(0, 8).map(tag => (
                <button
                  key={tag}
                  onClick={() => handleTagFilter(tag)}
                  className={`popular-tag ${selectedTag === tag ? 'active' : ''}`}
                >
                  #{tag}
                </button>
              ))}
            </div>
          </div>

          {/* Advertisement */}
          <div className="sidebar-section ad-section">
            <AdCard />
          </div>

          {/* Newsletter Signup */}
          <div className="sidebar-section newsletter-section">
            <h4>Stay Updated</h4>
            <p>Get the latest tech insights delivered to your inbox</p>
            <div className="newsletter-form">
              <input
                type="email"
                placeholder="Enter your email"
                className="newsletter-input"
              />
              <button className="newsletter-btn">Subscribe</button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default TechPage;
